import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft, ArrowRight, Check, Calendar, MapPin, GraduationCap, User, Sparkles,
    AlertTriangle, Info, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STEPS = ["Formation", "Date & horaires", "Salle", "Formateur", "Récap"];

function Stepper({ current }) {
    return (
        <div className="flex items-center gap-3" data-testid="wizard-stepper">
            {STEPS.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                        i < current ? "bg-green-600 text-white" :
                        i === current ? "bg-brand-orange text-white" :
                        "border border-brand-line bg-white text-brand-smoke"
                    }`}>
                        {i < current ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </div>
                    <div className={`text-xs font-semibold uppercase tracking-wider ${i <= current ? "text-brand-ink" : "text-brand-smoke"}`}>
                        {s}
                    </div>
                    {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-brand-smoke" />}
                </div>
            ))}
        </div>
    );
}

function ScoreBar({ value, max }) {
    return (
        <div className="h-1.5 w-12 rounded-full bg-brand-line">
            <div className="h-full rounded-full bg-brand-orange" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
        </div>
    );
}

export default function AdminSessionWizard() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [params] = useSearchParams();

    // Step 1
    const [formations, setFormations] = useState([]);
    const [formationId, setFormationId] = useState("");

    // Step 2
    const [date, setDate] = useState(params.get("date") || "");
    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("17:00");

    // Step 3
    const [salles, setSalles] = useState([]);
    const [salleId, setSalleId] = useState("");
    const [seats, setSeats] = useState(12);

    // Step 4
    const [candidates, setCandidates] = useState([]);
    const [nonQualified, setNonQualified] = useState([]);
    const [showNonQualified, setShowNonQualified] = useState(false);
    const [formateurId, setFormateurId] = useState(params.get("formateur_id") || "");
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [methodologyOpen, setMethodologyOpen] = useState(false);

    // Submitting
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/formations").then((r) => setFormations(r.data || [])).catch(() => {});
        api.get("/admin/salles").then((r) => setSalles(r.data || [])).catch(() => {});
    }, []);

    // Auto load candidates when entering step 4
    useEffect(() => {
        if (step !== 3 || !date) return;
        setLoadingCandidates(true);
        const params = new URLSearchParams({ date });
        if (salleId) params.set("salle_id", salleId);
        if (formationId) params.set("formation_id", formationId);
        api.get(`/admin/sessions/wizard/compatible-formateurs?${params.toString()}`)
            .then((r) => {
                setCandidates(r.data.candidates || []);
                setNonQualified(r.data.non_qualified || []);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoadingCandidates(false));
    }, [step, date, salleId, formationId]);

    const formation = formations.find((f) => f.id === formationId);
    const salle = salles.find((s) => s.id === salleId);
    const candidate = [...candidates, ...nonQualified].find((c) => c.formateur.id === formateurId);

    const canNext = () => {
        if (step === 0) return !!formationId;
        if (step === 1) return !!date && !!startTime && !!endTime;
        if (step === 2) return !!salleId;
        if (step === 3) return !!formateurId;
        return true;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                formation_slug: formation?.slug,
                start_date: date,
                end_date: date,
                start_time: startTime,
                end_time: endTime,
                salle_id: salleId,
                formateur_id: formateurId,
                total_seats: Number(seats),
                status: "open",
            };
            await api.post("/admin/sessions", payload);
            toast.success("Session créée + notification formateur (mockée)");
            navigate(`/admin/sessions`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-6 py-10" data-testid="page-session-wizard">
            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                Wizard
            </div>
            <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Créer une session</h1>
            <div className="mt-6"><Stepper current={step} /></div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.25 }}
                    className="mt-8 rounded-3xl border border-brand-line bg-white p-8 shadow-premium"
                >
                    {step === 0 && (
                        <div data-testid="wizard-step-formation">
                            <div className="flex items-center gap-3">
                                <GraduationCap className="h-5 w-5 text-brand-orange" />
                                <h2 className="font-display text-2xl">Choisissez la formation</h2>
                            </div>
                            <select
                                value={formationId}
                                onChange={(e) => setFormationId(e.target.value)}
                                data-testid="wizard-formation-select"
                                className="mt-6 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange"
                            >
                                <option value="">— Sélectionner une formation —</option>
                                {formations.map((f) => (
                                    <option key={f.id} value={f.id}>
                                        {f.name} · {f.cursus_slug}
                                    </option>
                                ))}
                            </select>
                            {formation && (
                                <div className="mt-5 rounded-xl bg-[#FAFAFA] p-4 text-sm">
                                    <div className="font-semibold">{formation.name}</div>
                                    <div className="text-brand-smoke">Durée {formation.duration_hours || 7}h · Prix {formation.price_eur || 390}€</div>
                                    <p className="mt-2 text-xs text-brand-ink/70">{formation.short_description}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 1 && (
                        <div data-testid="wizard-step-date">
                            <div className="flex items-center gap-3">
                                <Calendar className="h-5 w-5 text-brand-orange" />
                                <h2 className="font-display text-2xl">Date et horaires</h2>
                            </div>
                            <div className="mt-6 grid gap-4 md:grid-cols-3">
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Date *</label>
                                    <input
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        data-testid="wizard-date-input"
                                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Début</label>
                                    <input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Fin</label>
                                    <input
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div data-testid="wizard-step-salle">
                            <div className="flex items-center gap-3">
                                <MapPin className="h-5 w-5 text-brand-orange" />
                                <h2 className="font-display text-2xl">Choisissez la salle</h2>
                            </div>

                            {(() => {
                                const priority = salles.filter((s) => s.is_priority_city);
                                const others = salles.filter((s) => !s.is_priority_city);
                                const renderCard = (s) => (
                                    <button
                                        key={s.id}
                                        onClick={() => setSalleId(s.id)}
                                        data-testid={`wizard-salle-${s.id}`}
                                        className={`relative rounded-xl border p-4 text-left transition ${
                                            salleId === s.id
                                                ? "border-brand-orange bg-brand-orange/5"
                                                : s.is_priority_city
                                                    ? "border-brand-orange/30 bg-white hover:border-brand-orange"
                                                    : "border-brand-line bg-white hover:border-brand-orange/40"
                                        }`}
                                    >
                                        {s.is_priority_city && (
                                            <span className="absolute -top-2 right-3 rounded-full bg-brand-orange px-2 py-0.5 text-[9px] font-semibold uppercase text-white">🎯 Cible</span>
                                        )}
                                        <div className="font-semibold">{s.name}</div>
                                        <div className="text-xs text-brand-smoke">{s.city} · {s.capacity || "?"} places</div>
                                    </button>
                                );
                                return (
                                    <>
                                    {priority.length > 0 && (
                                        <div className="mt-6" data-testid="wizard-salles-priority">
                                            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-brand-orange">🎯 Villes cibles Cursus Physio</div>
                                            <div className="mt-3 grid gap-3 md:grid-cols-2">{priority.map(renderCard)}</div>
                                        </div>
                                    )}
                                    {others.length > 0 && (
                                        <div className="mt-6" data-testid="wizard-salles-others">
                                            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-brand-smoke">📍 Autres villes</div>
                                            <div className="mt-3 grid gap-3 md:grid-cols-2">{others.map(renderCard)}</div>
                                        </div>
                                    )}
                                    {salles.length === 0 && (
                                        <div className="mt-6 rounded-xl border border-dashed border-brand-line bg-[#FAFAFA] p-6 text-center text-sm text-brand-smoke">
                                            Aucune salle disponible. Créez d'abord une salle dans /admin/salles.
                                        </div>
                                    )}
                                    </>
                                );
                            })()}
                            <div className="mt-5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Places à ouvrir</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={seats}
                                    onChange={(e) => setSeats(e.target.value)}
                                    className="mt-2 w-32 rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div data-testid="wizard-step-formateur">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <User className="h-5 w-5 text-brand-orange" />
                                    <h2 className="font-display text-2xl">Formateur compatible</h2>
                                </div>
                                <button
                                    onClick={() => setMethodologyOpen(!methodologyOpen)}
                                    data-testid="wizard-show-methodology"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
                                >
                                    <Info className="h-3 w-3" /> Comment est calculé le score ?
                                </button>
                            </div>
                            {methodologyOpen && (
                                <div className="mt-4 rounded-xl bg-[#FAFAFA] p-4 text-xs text-brand-ink/80" data-testid="wizard-methodology">
                                    <strong>Filtre dur</strong> — seuls les formateurs ayant déclaré pouvoir animer cette formation
                                    (étape "Mes formations enseignables") sont listés comme candidats. Les autres sont relégués
                                    en bas dans <em>Formateurs non qualifiés</em>, à forcer uniquement avec confirmation explicite.
                                    <br /><br />
                                    <strong>Score d'optimisation /100</strong> = 40 (proximité géo : ville couverte + rayon vs ville salle, dégradé linéaire à 0 sur 200 km)
                                    + 20 (disponibilité : journée=20, demi=10, indispo=0)
                                    + 20 (performance : satisfaction moyenne /100 × 0.2)
                                    + 20 (taux de remplissage moyen sur ses 5 dernières sessions).
                                    Tri par score décroissant. Compatible = qualifié ET geo&gt;0 ET dispo&gt;0 ET pas de conflit.
                                </div>
                            )}
                            {loadingCandidates ? (
                                <div className="mt-8 text-center text-brand-smoke">Calcul des scores…</div>
                            ) : (
                                <>
                                <div className="mt-5 space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="wizard-candidates-qualified">
                                    {candidates.length === 0 && (
                                        <div className="rounded-xl border border-brand-line bg-[#FAFAFA] p-6 text-center text-sm text-brand-smoke">
                                            Aucun formateur n'a déclaré pouvoir animer cette formation.
                                            Forcez un formateur depuis la liste ci-dessous si nécessaire.
                                        </div>
                                    )}
                                    {candidates.map((c) => {
                                        const f = c.formateur;
                                        const selected = formateurId === f.id;
                                        return (
                                            <button
                                                key={f.id}
                                                onClick={() => {
                                                    if (!c.compatible && !window.confirm("Ce formateur n'est pas optimal (zone géographique, disponibilité ou conflit). Forcer la sélection ?")) return;
                                                    setFormateurId(f.id);
                                                }}
                                                data-testid={`wizard-candidate-${f.email}`}
                                                className={`flex w-full items-start gap-4 rounded-xl border p-4 text-left transition ${
                                                    selected ? "border-brand-orange bg-brand-orange/5" :
                                                    c.compatible ? "border-brand-line bg-white hover:border-brand-orange/40" :
                                                    "border-brand-line bg-[#FAFAFA] opacity-70"
                                                }`}
                                            >
                                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 font-display text-lg text-brand-orange">
                                                    {(f.first_name || "?")[0]}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-semibold">{f.first_name} {f.last_name}</span>
                                                        {c.compatible && (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                                                                <Check className="h-2.5 w-2.5" /> Compatible
                                                            </span>
                                                        )}
                                                        {c.qualified_for_formation && (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-semibold text-brand-orange">
                                                                Qualifié
                                                            </span>
                                                        )}
                                                        {c.blockers?.already_assigned && (
                                                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                                                                Conflit : {c.conflict?.formation_name}
                                                            </span>
                                                        )}
                                                        {c.blockers?.not_available && (
                                                            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">
                                                                Indispo
                                                            </span>
                                                        )}
                                                        {c.blockers?.out_of_zone && (
                                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
                                                                Hors zone
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-brand-smoke md:grid-cols-4">
                                                        <span title={c.breakdown.geo.label}>📍 {c.breakdown.geo.label}</span>
                                                        <span title={c.breakdown.dispo.label}>🗓 {c.breakdown.dispo.label}</span>
                                                        <span title={c.breakdown.perf.label}>⭐ {c.breakdown.perf.label}</span>
                                                        <span title={c.breakdown.fill.label}>📊 {c.breakdown.fill.label}</span>
                                                    </div>
                                                </div>
                                                <div className="flex shrink-0 flex-col items-end gap-1">
                                                    <div className="font-display text-3xl font-semibold text-brand-orange">{c.score}<span className="text-xs text-brand-smoke">/100</span></div>
                                                    <ScoreBar value={c.score} max={100} />
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {nonQualified.length > 0 && (
                                    <div className="mt-6 border-t border-brand-line pt-5" data-testid="wizard-non-qualified-section">
                                        <button
                                            type="button"
                                            onClick={() => setShowNonQualified((s) => !s)}
                                            data-testid="wizard-toggle-non-qualified"
                                            className="flex w-full items-center justify-between rounded-xl bg-[#FAFAFA] px-4 py-3 text-left hover:bg-brand-line/30"
                                        >
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4 text-brand-smoke" />
                                                <span className="text-sm font-semibold">
                                                    Formateurs non qualifiés pour cette formation
                                                </span>
                                                <span className="rounded-full bg-brand-line px-2 py-0.5 text-[10px] text-brand-smoke">
                                                    {nonQualified.length}
                                                </span>
                                            </div>
                                            <ChevronRight className={`h-4 w-4 text-brand-smoke transition-transform ${showNonQualified ? "rotate-90" : ""}`} />
                                        </button>
                                        {showNonQualified && (
                                            <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                                {nonQualified.map((c) => {
                                                    const f = c.formateur;
                                                    const selected = formateurId === f.id;
                                                    return (
                                                        <button
                                                            key={f.id}
                                                            onClick={() => {
                                                                if (!window.confirm(`⚠️ ${f.first_name} ${f.last_name} n'a pas déclaré pouvoir animer cette formation (${c.taught_count} formation(s) déclarée(s) au total). Confirmer la sélection forcée ?`)) return;
                                                                setFormateurId(f.id);
                                                            }}
                                                            data-testid={`wizard-non-qualified-${f.email}`}
                                                            className={`flex w-full items-start gap-3 rounded-xl border border-dashed p-3 text-left transition opacity-90 ${
                                                                selected ? "border-red-400 bg-red-50" : "border-brand-line bg-white hover:border-red-300"
                                                            }`}
                                                        >
                                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 font-display text-base text-gray-500">
                                                                {(f.first_name || "?")[0]}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="font-semibold text-brand-ink/80">{f.first_name} {f.last_name}</span>
                                                                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                                                                        ⚠️ Non listé comme compétent — confirmation requise
                                                                    </span>
                                                                </div>
                                                                <div className="mt-1 text-[11px] text-brand-smoke">
                                                                    {c.taught_count > 0 ? `${c.taught_count} formation(s) déclarée(s)` : "Aucune formation déclarée"}
                                                                </div>
                                                            </div>
                                                            <div className="flex shrink-0 flex-col items-end gap-1">
                                                                <div className="font-display text-xl font-semibold text-brand-smoke">{c.score}<span className="text-[10px]">/100</span></div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                                </>
                            )}
                        </div>
                    )}

                    {step === 4 && (
                        <div data-testid="wizard-step-recap">
                            <div className="flex items-center gap-3">
                                <Sparkles className="h-5 w-5 text-brand-orange" />
                                <h2 className="font-display text-2xl">Récapitulatif</h2>
                            </div>
                            <dl className="mt-6 space-y-3 text-sm">
                                <div className="flex justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Formation</dt>
                                    <dd className="font-semibold">{formation?.name}</dd>
                                </div>
                                <div className="flex justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Date</dt>
                                    <dd className="font-semibold">{date} · {startTime}–{endTime}</dd>
                                </div>
                                <div className="flex justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Salle</dt>
                                    <dd className="font-semibold">{salle?.name} · {salle?.city}</dd>
                                </div>
                                <div className="flex justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Places</dt>
                                    <dd className="font-semibold">{seats}</dd>
                                </div>
                                <div className="flex justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Formateur</dt>
                                    <dd className="font-semibold">{candidate?.formateur.first_name} {candidate?.formateur.last_name} <span className="text-brand-orange">({candidate?.score}/100)</span></dd>
                                </div>
                            </dl>
                            <div className="mt-6 rounded-xl bg-orange-50 p-4 text-sm text-brand-ink/80">
                                Le formateur recevra une notification email mockée à la création.
                            </div>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-between">
                <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    data-testid="wizard-back"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange disabled:opacity-40"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Précédent
                </button>
                {step < 4 ? (
                    <button
                        onClick={() => canNext() && setStep((s) => s + 1)}
                        disabled={!canNext()}
                        data-testid="wizard-next"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-50"
                    >
                        Suivant <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                ) : (
                    <button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="wizard-submit"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-50"
                    >
                        <Sparkles className="h-3.5 w-3.5" />
                        {submitting ? "Création…" : "Créer la session"}
                    </button>
                )}
            </div>
        </div>
    );
}
